<script setup lang="ts">
  import { nextTick, ref } from 'vue';
  import { Switch } from '@/shared/ui';
  import { correctedWordOf, fillCountLabel, type FillLogsResponseView } from '../model/fill-logs-timeline';
  import FillLogsTimelineItem from './FillLogsTimelineItem.vue';

  const props = defineProps<{
    timeline: FillLogsResponseView[];
    selectedNumber: number;
    showHighlights: boolean;
    correctedLegend: string;
  }>();

  const emit = defineEmits<{
    (e: 'select', number: number): void;
    (e: 'newer'): void;
    (e: 'older'): void;
    (e: 'update:showHighlights', value: boolean): void;
  }>();

  const listRef = ref<HTMLElement | null>(null);

  async function focusSelected(): Promise<void> {
    await nextTick();
    listRef.value
      ?.querySelector<HTMLElement>(`[data-number="${props.selectedNumber}"]`)
      ?.focus();
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    if (event.key === 'ArrowUp') emit('newer');
    else emit('older');
    focusSelected();
  }
</script>

<template>
  <aside
    class="flex min-h-0 w-[360px] flex-1 flex-col overflow-hidden rounded-lg border border-gray-02 bg-white-01"
    aria-label="Preenchimentos do Registro"
    data-testid="fill-logs-sidebar"
  >
    <header class="shrink-0 border-b border-gray-02 px-4 py-3">
      <h2 class="flex items-baseline justify-between text-sm font-semibold text-gray-07">
        Preenchimentos
        <small
          class="text-xs font-normal text-gray-03"
          data-testid="fill-logs-count"
        >
          {{ fillCountLabel(timeline.length) }}
        </small>
      </h2>
    </header>

    <ol
      ref="listRef"
      class="min-h-0 flex-1 overflow-y-auto py-2"
      aria-label="Linha do tempo das respostas"
      data-testid="fill-logs-timeline"
      @keydown="onKeydown"
    >
      <FillLogsTimelineItem
        v-for="(response, index) in timeline"
        :key="response.number"
        :response="response"
        :selected="response.number === selectedNumber"
        :first="index === 0"
        :last="index === timeline.length - 1"
        :corrected-word="correctedWordOf(response)"
        @select="emit('select', $event)"
      />
    </ol>

    <footer class="flex shrink-0 flex-col gap-3 border-t border-gray-02 p-4">
      <label class="flex cursor-pointer items-center gap-2.5 text-sm text-gray-07">
        <Switch
          size="lg"
          :model-value="showHighlights"
          data-testid="fill-logs-highlight-switch"
          @update:model-value="emit('update:showHighlights', $event)"
        />
        Destacar mudanças
      </label>
      <div
        v-if="showHighlights"
        class="flex flex-col gap-1.5 text-xs text-gray-03"
        data-testid="fill-logs-legend"
      >
        <span class="flex items-center gap-2">
          <i
            class="size-3.5 shrink-0 rounded-[3px] border border-change-added-border bg-change-added"
            data-testid="fill-logs-legend-filled"
          />
          Preenchido nesta resposta
        </span>
        <span class="flex items-center gap-2">
          <i
            class="size-3.5 shrink-0 rounded-[3px] border border-change-fixed-border bg-change-fixed"
            data-testid="fill-logs-legend-corrected"
          />
          {{ correctedLegend }}
        </span>
      </div>
    </footer>
  </aside>
</template>
